import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import type { BuchungsheldPlugin } from '@adornis/buchungsheld-db/plugins/plugin-types.js';
import { getPluginForScope, registeredPlugins } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import type { BhAdminProductWaitingListPriorityOverview } from '../../../../components/bh-admin-product-waitinglist-priority-overview.js';
import { EinrichtungSettingsAdapter } from './EinrichtungSettingsAdapter.js';

@customElement('bh-page-admin-einrichtung-settings-general-info')
export class BHPageAdminEinrichtungSettingsGeneralInfo extends EinrichtungSettingsAdapter {
  @state()
  private readonly userController = new RXController(this, currentUser<User>());

  // eslint-disable-next-line max-lines-per-function
  override render() {
    const groupedPlugins = {} as Record<string, Record<string, BuchungsheldPlugin>>;
    for (const plugin of registeredPlugins.values()) {
      const [groupName, name] = plugin.id.split(/::([^:]+)$/);
      if (!groupName) throw new Error('groupName not found');
      if (!name) throw new Error('name not found');

      if (!groupedPlugins[groupName]) {
        groupedPlugins[groupName] = {};
      }

      groupedPlugins[groupName]![name] = plugin;
    }

    const selectedPlugins = this._form.document?.plugins || [];

    return html`
      <x-flex space="xl">
        <bh-breadcrumb
          .items=${[
            {
              content: this._translation.translate('settings', 'name'),
              href: `/admin/institution-details/${this.institutionID.value}/settings`,
            },
            { content: this._translation.translate('settings', 'general_information'), active: true },
          ]}
          .additionalAction=${html`
            <tr-button-small
              icon="save"
              .action=${async () => {
                await this._form.validate({ recurse: true, validateResolved: true, aggregateErrors: true });
                await this._saveWaitingListPriority();
                this._waitingListPriorityOverview.value?.requestUpdate();
              }}
            ></tr-button-small>
          `}
        ></bh-breadcrumb>

        ${this.userController.value?.isSuperAdmin
          ? html`
              <x-basic-card padding="md">
                <x-flex space="md">
                  <x-h2> Super Admin Settings</x-h2>

                  <x-grid
                    ${css({
                      gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                      gridTemplateRows: 'repeat(auto-fill, minmax(80px, 1fr))',
                    })}
                    space="md"
                  >
                    ${Object.entries(groupedPlugins).map(
                      ([groupName, plugins]) => html`
                        <tr-dropdown-selection
                          .value=${selectedPlugins
                            .filter(it => it.startsWith(groupName))
                            .map(it => it.split(/::([^:]+)$/).at(1))
                            ?.at(0) ?? 'default'}
                          placeholder=${groupName}
                          .selectables=${Object.keys(plugins)}
                          @value-picked=${(e: ValueEvent<string>) => {
                            const pluginName = e.detail.value;
                            const pluginID = `${groupName}::${pluginName}`;
                            const newPluginList =
                              this._form.document?.plugins?.filter(it => !it.startsWith(groupName)) ?? [];

                            if (pluginName !== 'default') newPluginList.push(pluginID);
                            this._form.document!.plugins = newPluginList;
                          }}
                        ></tr-dropdown-selection>
                      `,
                    )}
                  </x-grid>

                  <tr-checkbox
                    ${this._form.field('_feature_counselings')}
                    label="Einzelberatungen aktivieren"
                  ></tr-checkbox>
                </x-flex>
              </x-basic-card>
            `
          : nothing}

        <x-basic-card padding="md">
          <x-flex space="md">
            <x-h2> ${this._translation.translate('settings', 'basic_information')} </x-h2>
            <x-flex horizontal crossaxis-start wrap space="sm">
              <x-flex
                ${acss({
                  aspectRatio: '1 / 1',
                  width: `250px`,
                  height: '250px',
                  cursor: 'pointer',
                  position: 'relative',
                  '> *': {
                    height: '100%',
                  },
                })}
                @click=${async () => this._handleFileUpload('logoID')}
              >
                <tr-tooltip
                  placement="bottom"
                  .text=${this._translation.translate('settings', 'upload_image')}
                ></tr-tooltip>

                ${this._form.document?.logoID
                  ? html`
                      <x-image
                        disabled
                        .value=${AdornisFile.getServeLink(this._form.document.logoID)}
                        loading="lazy"
                        mode="contain"
                      >
                      </x-image>
                    `
                  : html`
                      <tr-flex
                        ${css({
                          background: this.colors.accent.shade(200),
                        })}
                        center
                        crossaxis-center
                      >
                        <tr-icon huge ${css({ color: '#fff', fontSize: '3em' })}> apartment </tr-icon>
                      </tr-flex>
                    `}
              </x-flex>

              <x-flex space="sm" flex ${css({ minWidth: '300px' })}>
                <tr-input
                  ${this._form.field('name')}
                  placeholder="Name"
                  info=${this._translation.translate('fieldInfo', 'institution', 'name')}
                ></tr-input>

                <tr-input
                  ${this._form.field('shortDescription')}
                  placeholder=${this._translation.translate('settings', 'short_description')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'shortDescription')}
                ></tr-input>

                <tr-input
                  type="number"
                  ${this._form.field('sortingWeight')}
                  placeholder=${this._translation.translate('field', 'sortingWeight')}
                  info=${this._translation.translate('fieldInfo', 'sortingWeight')}
                ></tr-input>

                <tr-input
                  ${this._form.field('ccMail')}
                  placeholder=${this._translation.translate('settings', 'ccMail')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'ccMail')}
                ></tr-input>

                <tr-input
                  type="email"
                  step="1"
                  ${this._form.field('settings', 'replyToMailAddress')}
                  placeholder=${this._translation.translate('field', 'replyToMailAddress')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'replyToMailAddress')}
                ></tr-input>

                <tr-checkbox
                  ${this._form.field('isLDAPOnly')}
                  label=${this._translation.translate('settings', 'is_ldap_registration')}
                ></tr-checkbox>
                <tr-checkbox
                  ${this._form.field('sortProductsByTime')}
                  label=${this._translation.translate('settings', 'sort_by_date')}
                ></tr-checkbox>
              </x-flex>
            </x-flex>
          </x-flex>
        </x-basic-card>
        <x-flex horizontal wrap space="lg">
          <x-basic-card padding="md" flex ${css({ minWidth: '300px' })}>
            <x-flex space="md">
              <x-h2> ${this._translation.translate('settings', 'workshop_info')} </x-h2>
              <x-flex space="sm">
                <tr-text> ${this._translation.translate('settings', 'default_value_expl')} </tr-text>
                <tr-input
                  ${this._form.field('settings', 'courseLocation')}
                  type="text"
                  placeholder=${this._translation.translate('settings', 'course_location')}
                ></tr-input>
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('settings', 'capacity')}
                  placeholder="${this._translation.translate('field', 'capacity')}"
                ></tr-input>
                <tr-input
                  ${this._form.field('settings', 'blockSignOffDaysInAdvance')}
                  type="number"
                  step="1"
                  placeholder=${this._translation.translate('settings', 'cancel_deadline')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'cancellationDeadline')}
                ></tr-input>
                <tr-input
                  ${this._form.field('settings', 'blockAutomaticMoveUpDaysInAdvance')}
                  type="number"
                  step="1"
                  placeholder=${this._translation.translate('settings', 'move_up_notice')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'waitinglistDeadline')}
                ></tr-input>
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('settings', 'firstReminderLeadTime')}
                  placeholder=${this._translation.translate('settings', 'reminder_mail_1')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'firstReminderMail')}
                ></tr-input>
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('settings', 'secondReminderLeadTime')}
                  placeholder=${this._translation.translate('settings', 'reminder_mail_2')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'secondReminderMail')}
                ></tr-input>
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('settings', 'firstAppointmentReminderLeadTime')}
                  placeholder=${this._translation.translate('settings', 'appointment_reminder_mail_1')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'firstReminderMail')}
                ></tr-input>
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('settings', 'secondAppointmentReminderLeadTime')}
                  placeholder=${this._translation.translate('settings', 'appointment_reminder_mail_2')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'secondReminderMail')}
                ></tr-input>

                <!-- infoLink -->
                <tr-input ${this._form.field('settings', 'infoLink')} placeholder="Infolink"></tr-input>

                <!-- qrcode validity -->
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('settings', 'qrCodeValidityInMinutes')}
                  placeholder=${this._translation.translate('settings', 'qrcode_valid')}
                  postfix="Min."
                ></tr-input>
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('maximumParallelSignups')}
                  placeholder=${this._translation.translate('settings', 'max_simul_reg')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'maxSimultaneousRegistrations')}
                ></tr-input>
                <tr-input
                  type="email"
                  info=${this._translation.translate('fieldInfo', 'institution', 'secondReminderMail')}
                  placeholder=${this._translation.translate('settings', 'internAccountingEmail') + ' 1'}
                  ${this._form.field('settings', 'interAccountingEmail1')}
                ></tr-input>
                <tr-input
                  type="email"
                  info=${this._translation.translate('fieldInfo', 'institution', 'secondReminderMail')}
                  placeholder=${this._translation.translate('settings', 'internAccountingEmail') + ' 2'}
                  ${this._form.field('settings', 'interAccountingEmail2')}
                ></tr-input>
                <tr-input
                  type="email"
                  info=${this._translation.translate('fieldInfo', 'institution', 'secondReminderMail')}
                  placeholder=${this._translation.translate('settings', 'internAccountingEmail') + ' 3'}
                  ${this._form.field('settings', 'interAccountingEmail3')}
                ></tr-input>
                <tr-input
                  type="number"
                  step="1"
                  ${this._form.field('settings', 'sendInvoiceDaysInAdvance')}
                  placeholder=${this._translation.translate('settings', 'sendInvoiceDaysInAdvance')}
                  info=${this._translation.translate('fieldInfo', 'institution', 'sendInvoiceDaysInAdvance')}
                ></tr-input>
              </x-flex>
            </x-flex>
          </x-basic-card>
        </x-flex>

        <!-- waitinglist priority -->
        <x-basic-card padding="md"> ${this.renderWaitingListPriorityPart()} </x-basic-card>

        <!-- maintenance mode -->
        <x-basic-card padding="md">
          <x-flex space="md">
            <x-h2> ${this._translation.translate('settings', 'maintainance_mode')} </x-h2>
            <x-flex space="sm">
              <tr-checkbox
                ${this._form.field('maintenanceMode')}
                label=${this._translation.translate('settings', 'act_maintainance_mode')}
              ></tr-checkbox>
              <tr-tinymce
                @value-picked=${(e: ValueEvent<string>) => {
                  if (!this._form.document) return;

                  if (e.detail.value.replace(/<\/?[^>]+(>|$)/g, '').replace(/&[a-z]+;/g, '') === '') {
                    this._form.document.maintenanceModeRender = '';
                    console.log('set to empty string');
                  } else {
                    this._form.document.maintenanceModeRender = e.detail.value;
                  }
                }}
                placeholder=${this._translation.translate('settings', 'when_maintainance_on')}
                ${this._form.field('maintenanceModeRender')}
              ></tr-tinymce>
            </x-flex>
          </x-flex>
        </x-basic-card>
      </x-flex>
    `;
  }

  private async _handleFileUpload(logoKind: 'logoID' | 'brandLogoID') {
    if (!this._form.document) return;
    const files = await askForFile({
      acceptFileTypes: '.png, .img, .jpeg, .jpg',
      acceptMultipleFiles: false,
    });
    if (!files) return;
    const file = files[0];
    if (!file) return;

    const fileID = await AdornisFile.upload(file);
    this._form.document[logoKind] = fileID;
    this.requestUpdate();
  }

  // waiting-list-priority stuff
  private _waitingListPriorityOverview = createRef<BhAdminProductWaitingListPriorityOverview>();
  private renderWaitingListPriorityPart() {
    const institutionID = this.institutionID.value;
    if (!institutionID) return nothing;

    const priorityPlugin = getPluginForScope('registration::product-waitinglist-priority-rules', institutionID);
    const kinds = priorityPlugin.getRuleKinds();
    if (kinds.length === 0) return nothing;

    return html`
      </br>
      <bh-admin-waiting-list-priority-overview
        ${ref(this._waitingListPriorityOverview)}
        institution-id=${this.institutionID.value}
      ></bh-admin-waiting-list-priority-overview>
    `;
  }

  private async _saveWaitingListPriority() {
    return this._waitingListPriorityOverview.value?.saveAllPriorityRules(undefined);
  }
}
